import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, Loader, Settings } from 'lucide-react';
import LeadCard from '../leads/LeadCard';
import LeadDetailsPanel from '../leads/LeadDetailsPanel';
import CallScriptModal from '../leads/CallScriptModal';
import ScriptEditor from '../leads/ScriptEditor';
import Pagination from '../shared/Pagination';
import { useToast, ToastContainer } from '../shared/Toast';
import { getLeads } from '../../services/perfex-api';
import { initDatabase } from '../../../database/db';

/**
 * Leads Follow-ups Tab Component
 * Displays leads from Perfex CRM with inline editing and call script functionality
 */
const LeadsTab = () => {
    const [leads, setLeads] = useState([]);
    const [filteredLeads, setFilteredLeads] = useState([]);
    const [selectedLead, setSelectedLead] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalLeads, setTotalLeads] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showCallScript, setShowCallScript] = useState(false);
    const [showScriptEditor, setShowScriptEditor] = useState(false);
    const [dateFilter, setDateFilter] = useState('newest'); // newest, yesterday, thisweek, lastweek
    const { toasts, showToast, removeToast } = useToast();

    const itemsPerPage = 20;

    // Initialize database on mount
    useEffect(() => {
        initDatabase();
    }, []);

    // Load leads on mount and page change
    useEffect(() => {
        loadLeads();
    }, [currentPage]);

    // Filter leads based on date filter
    useEffect(() => {
        if (leads.length === 0) {
            setFilteredLeads([]);
            return;
        }

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() - today.getDay()); // Start of this week (Sunday)

        const lastWeekStart = new Date(thisWeekStart);
        lastWeekStart.setDate(lastWeekStart.getDate() - 7);
        const lastWeekEnd = new Date(thisWeekStart);
        lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);

        let filtered = [...leads];

        switch (dateFilter) {
            case 'newest':
                // Sort by date added (most recent first)
                filtered = filtered.sort((a, b) => {
                    const dateA = new Date(a.dateadded || a.date_added || 0);
                    const dateB = new Date(b.dateadded || b.date_added || 0);
                    return dateB - dateA;
                });
                break;
            case 'yesterday':
                filtered = filtered.filter(lead => {
                    const leadDate = new Date(lead.dateadded || lead.date_added);
                    const leadDay = new Date(leadDate.getFullYear(), leadDate.getMonth(), leadDate.getDate());
                    return leadDay.getTime() === yesterday.getTime();
                });
                break;
            case 'thisweek':
                filtered = filtered.filter(lead => {
                    const leadDate = new Date(lead.dateadded || lead.date_added);
                    return leadDate >= thisWeekStart && leadDate < today.setDate(today.getDate() + 1);
                });
                break;
            case 'lastweek':
                filtered = filtered.filter(lead => {
                    const leadDate = new Date(lead.dateadded || lead.date_added);
                    return leadDate >= lastWeekStart && leadDate <= lastWeekEnd;
                });
                break;
        }

        setFilteredLeads(filtered);
    }, [leads, dateFilter]);

    const loadLeads = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const result = await getLeads(currentPage, itemsPerPage);

            if (result.success) {
                // Handle different API response structures
                const leadData = result.data?.leads || result.data || [];
                const total = result.data?.total || leadData.length;

                setLeads(Array.isArray(leadData) ? leadData : []);
                setTotalLeads(total);

                // If current page is empty and we're not on page 1, go back a page
                if (leadData.length === 0 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                }
            } else {
                throw new Error(result.data?.message || 'Failed to load leads');
            }
        } catch (err) {
            console.error('Error loading leads:', err);
            setError(err.message || 'Failed to load leads from Perfex CRM');
            showToast('Failed to load leads: ' + err.message, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLeadSelect = (lead) => {
        setSelectedLead(lead);
    };

    const handleLeadUpdate = (updatedLead) => {
        // Update lead in list
        setLeads((prevLeads) =>
            prevLeads.map((lead) => (lead.id === updatedLead.id ? updatedLead : lead))
        );

        // Update selected lead
        setSelectedLead(updatedLead);

        showToast('Lead updated successfully', 'success');
    };

    const handleCallClick = () => {
        setShowCallScript(true);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        setSelectedLead(null); // Clear selection when changing pages
    };

    const handleRefresh = () => {
        setSelectedLead(null);
        loadLeads();
    };

    return (
        <div className="flex h-full bg-gray-50">
            {/* Left Panel - Lead List */}
            <div className="w-full md:w-2/5 lg:w-1/3 border-r border-gray-200 flex flex-col bg-white">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-green-600 to-blue-600 text-white">
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-xl font-bold">Leads Follow-ups</h2>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowScriptEditor(true)}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                title="Manage call scripts"
                            >
                                <Settings size={20} />
                            </button>
                            <button
                                onClick={handleRefresh}
                                disabled={isLoading}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
                                title="Refresh leads"
                            >
                                <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                            </button>
                        </div>
                    </div>
                    <p className="text-sm text-green-100 mb-3">
                        {filteredLeads.length} of {totalLeads} lead{totalLeads !== 1 ? 's' : ''}
                    </p>

                    {/* Date Filter Tabs */}
                    <div className="flex gap-2 flex-wrap">
                        <button
                            onClick={() => setDateFilter('newest')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                dateFilter === 'newest'
                                    ? 'bg-white text-green-700'
                                    : 'bg-white/20 hover:bg-white/30'
                            }`}
                        >
                            Newest
                        </button>
                        <button
                            onClick={() => setDateFilter('yesterday')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                dateFilter === 'yesterday'
                                    ? 'bg-white text-green-700'
                                    : 'bg-white/20 hover:bg-white/30'
                            }`}
                        >
                            Yesterday
                        </button>
                        <button
                            onClick={() => setDateFilter('thisweek')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                dateFilter === 'thisweek'
                                    ? 'bg-white text-green-700'
                                    : 'bg-white/20 hover:bg-white/30'
                            }`}
                        >
                            This Week
                        </button>
                        <button
                            onClick={() => setDateFilter('lastweek')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                                dateFilter === 'lastweek'
                                    ? 'bg-white text-green-700'
                                    : 'bg-white/20 hover:bg-white/30'
                            }`}
                        >
                            Last Week
                        </button>
                    </div>
                </div>

                {/* Lead List */}
                <div className="flex-1 overflow-y-auto">
                    {isLoading && leads.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-gray-500">
                                <Loader size={48} className="mx-auto mb-3 animate-spin text-green-600" />
                                <p>Loading leads...</p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="p-6">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
                                    <div>
                                        <h3 className="font-semibold text-red-800 mb-1">
                                            Error Loading Leads
                                        </h3>
                                        <p className="text-sm text-red-700">{error}</p>
                                        <button
                                            onClick={handleRefresh}
                                            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors"
                                        >
                                            Try Again
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : filteredLeads.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center text-gray-500 p-6">
                                <AlertCircle size={48} className="mx-auto mb-3 opacity-30" />
                                <p className="text-lg font-semibold mb-2">No Leads Found</p>
                                <p className="text-sm">
                                    {leads.length === 0
                                        ? 'There are no leads to display for the current page.'
                                        : `No leads found for ${
                                            dateFilter === 'newest' ? 'newest' :
                                            dateFilter === 'yesterday' ? 'yesterday' :
                                            dateFilter === 'thisweek' ? 'this week' : 'last week'
                                        }.`
                                    }
                                </p>
                                {currentPage > 1 && leads.length === 0 && (
                                    <button
                                        onClick={() => setCurrentPage(1)}
                                        className="mt-4 text-green-600 hover:text-green-700 font-semibold"
                                    >
                                        Go to First Page
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div>
                            {filteredLeads.map((lead) => (
                                <LeadCard
                                    key={lead.id}
                                    lead={lead}
                                    isSelected={selectedLead?.id === lead.id}
                                    onClick={() => handleLeadSelect(lead)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {!isLoading && !error && leads.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalItems={totalLeads}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>

            {/* Right Panel - Lead Details */}
            <div className="hidden md:block flex-1">
                <LeadDetailsPanel
                    lead={selectedLead}
                    onUpdate={handleLeadUpdate}
                    onCallClick={handleCallClick}
                />
            </div>

            {/* Mobile: Lead details as overlay */}
            {selectedLead && (
                <div className="md:hidden fixed inset-0 bg-white z-50 flex flex-col">
                    <div className="p-4 border-b border-gray-200 bg-green-600 text-white flex items-center justify-between">
                        <h2 className="text-lg font-bold">Lead Details</h2>
                        <button
                            onClick={() => setSelectedLead(null)}
                            className="px-4 py-2 bg-white/20 rounded-lg font-semibold hover:bg-white/30 transition-colors"
                        >
                            Back
                        </button>
                    </div>
                    <div className="flex-1 overflow-hidden">
                        <LeadDetailsPanel
                            lead={selectedLead}
                            onUpdate={handleLeadUpdate}
                            onCallClick={handleCallClick}
                        />
                    </div>
                </div>
            )}

            {/* Call Script Modal */}
            <CallScriptModal
                show={showCallScript}
                onClose={() => setShowCallScript(false)}
                lead={selectedLead}
                onEditScript={() => {
                    setShowCallScript(false);
                    setShowScriptEditor(true);
                }}
            />

            {/* Script Editor */}
            <ScriptEditor
                show={showScriptEditor}
                onClose={() => setShowScriptEditor(false)}
            />

            {/* Toast Notifications */}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </div>
    );
};

export default LeadsTab;
